import { Box, Button, Chip, Typography } from '@mui/material';
import { FC } from 'react';
import { useHistory } from 'react-router';
import { routes } from 'src/constants';

import { ColumnType, UserDto, UserOrganization } from 'src/types';
import { Table } from '../Table';
import { ActionsTableButton } from '../TablePage/ActionsTableButton';

interface Props {
    data: UserDto[];
    loading?: boolean;
}

const ORGANIZATION_TO_DISPLAY_PER_USER_LIMIT = 5;

export const UsersTable: FC<Props> = ({ data, loading }) => {
    const history = useHistory();

    const columns: ColumnType<UserDto>[] = [
        {
            title: 'ID',
            dataIndex: 'id'
        },
        {
            title: 'First name',
            dataIndex: 'firstName'
        },
        {
            title: 'Last name',
            dataIndex: 'lastName'
        },
        {
            title: 'Account status',
            dataIndex: 'status',
            render: value => <Chip label={value} />
        },
        {
            title: 'Organizations',
            dataIndex: 'organizations',
            width: '150px',
            render: (organizations: UserOrganization[]) => {
                const isMoreThanLimit =
                    organizations.length >
                    ORGANIZATION_TO_DISPLAY_PER_USER_LIMIT;

                const organizationsToDisplay = isMoreThanLimit
                    ? organizations.slice(0, 5)
                    : organizations;

                return (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {organizationsToDisplay.map((org: UserOrganization) => (
                            <Chip
                                key={org.id}
                                label={org.name}
                                variant="outlined"
                            />
                        ))}

                        {isMoreThanLimit && (
                            <Typography>
                                {organizations.length -
                                    ORGANIZATION_TO_DISPLAY_PER_USER_LIMIT}{' '}
                                more
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            title: '',
            dataIndex: 'actions',
            align: 'center',
            width: '60px',
            render: (_, { id }) => (
                <ActionsTableButton
                    menuItems={[
                        {
                            title: 'Edit',
                            onClick: () =>
                                history.push(routes.managementUser(id))
                        }
                    ]}
                />
            ),
            mobile: {
                render: (_data, { id }) => (
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => history.push(routes.managementUser(id))}
                    >
                        Edit
                    </Button>
                ),
                fullWidth: true
            }
        }
    ];

    return (
        <Table
            columns={columns}
            data={data}
            loading={loading}
            mobile={{
                renderSummary: row => `${row.firstName} ${row.lastName}`
            }}
        />
    );
};
